import Vector from "./Auxiliar/Vector";

export default class KinematicsManager {
    // Public:
    constructor(cParams) {
        this.saveParams(cParams);
        this.initGravitationalCenter();
        this.restartVelocityDecay();
    }

    compute() {
        if (this.isDragOn()) this.restartVelocityDecay();
        this.computeKinematicMagnitudes();
        if (this.isSystemReallocating()) this.decreaseVelocityDecay();
        else if (this.isSystemSpinning()) this.increaseVelocityDecay();
        else this.persueTargetVelocityDecay();
        this.preventNodesFromScaping();
    }

    setGravitationalCenter(center) {
        this._center = center;
    }

    //Private:
    saveParams(cParams) {
        this._elementId = cParams.elementId;
        this._nodes = cParams.nodes;
        this._simulationFunc = cParams.simulationFunc;
        this._initialVelocityDecay = cParams.velocityDecayParams.initialValue;
        this._velocityDecayIncreaseRate = cParams.velocityDecayParams.increaseRate;
        this._velocityDecayDecreaseRate = cParams.velocityDecayParams.decreaseRate;
        this._targetVelocityDecay = cParams.velocityDecayParams.target;
        this._maxVelocityDecay = cParams.velocityDecayParams.maxValue;
        this._minVelocityDecay = cParams.velocityDecayParams.minValue;
        this._vorticityThreshold = cParams.vorticityThreshold;
        this._reallocatingDetectionFactor = cParams.reallocatingDetectionFactor;
        this._maxVelocity = cParams.maxVelocity;
    }

    computeKinematicMagnitudes() {
        let velocityArray = [];
        let vorticity = 0;
        let angularFlow = 0;
        let radialFlow = 0;
        let nodes = this._nodes;
        const N = nodes.length;
        const x0 = this._center.x;
        const y0 = this._center.y;
        for (let i = 0; i < N; i++) {
            const node = nodes[i];
            const x = node.x;
            const y = node.y;
            const vx = node.vx;
            const vy = node.vy;
            let r = new Vector([x - x0, y - y0]);
            let v = new Vector([vx, vy]);
            let u = r.getNormalized();
            const R = r.computeModulus();
            const L = v.computeCrossProduct(u);
            const w = L / R;
            const s = v.computeDotProduct(u);
            const V = v.computeModulus();
            vorticity += w;
            angularFlow += L;
            radialFlow += s;
            velocityArray[i] = V;
        }
        this._meanVorticity = vorticity / N;
        this._meanAngularFlow = angularFlow / N;
        this._meanRadialFlow = radialFlow / N;
        this._velocityArray = velocityArray;
    }

    increaseVelocityDecay() {
        if (this.canVelocityDecayIncrease()) {
            this._velocityDecay += this._velocityDecayIncreaseRate;
            this.updateVelocityDecay();
        }
    }

    decreaseVelocityDecay() {
        if (this.canVelocityDecayDecrease()) {
            this._velocityDecay -= this._velocityDecayDecreaseRate;
            this.updateVelocityDecay();
        }
    }

    persueTargetVelocityDecay() {
        const target = this._targetVelocityDecay;
        const vD = this._velocityDecay;
        if (vD < target) this.increaseVelocityDecay();
        else this.decreaseVelocityDecay();
    }

    isSystemSpinning() {
        const vorticityThreshold = this._vorticityThreshold;
        const vorticityMagnitude = Math.abs(this._meanVorticity);
        const angularMagnitude = Math.abs(this._meanAngularFlow);
        const radialMagnitude = Math.abs(this._meanRadialFlow);
        const vorticityIsHigherThanThreshold = vorticityMagnitude > vorticityThreshold;
        const angularFlowIsGreaterThanRadial = angularMagnitude > radialMagnitude;
        if (vorticityIsHigherThanThreshold && angularFlowIsGreaterThanRadial) return true;
        else return false;
    }

    isSystemReallocating() {
        const k = this._reallocatingDetectionFactor;
        const angularMagnitude = Math.abs(this._meanAngularFlow);
        const radialMagnitude = Math.abs(this._meanRadialFlow);
        const axialFlowIsGreaterThanAngular = radialMagnitude > k * angularMagnitude;
        if (axialFlowIsGreaterThanAngular) return true;
        else return false;
    }

    initGravitationalCenter() {
        const center = {x: 0, y: 0};
        this.setGravitationalCenter(center);
    }

    restartVelocityDecay() {
        this._velocityDecay = this._initialVelocityDecay;
        this.updateVelocityDecay();
    }

    isDragOn() {
        const elementId = this._elementId;
        const element = document.getElementById(elementId);
        if (element) {
            const itIs = element.isDragOn;
            return itIs;
        } else {
            return false;
        }
    }

    canVelocityDecayIncrease() {
        const maxVD = this._maxVelocityDecay;
        if (this._velocityDecay + this._velocityDecayIncreaseRate < maxVD) return true;
        else return false;
    }

    canVelocityDecayDecrease() {
        const minVD = this._minVelocityDecay;
        if (this._velocityDecay - this._velocityDecayDecreaseRate > minVD) return true;
        else return false;
    }

    updateVelocityDecay() {
        const vD = this._velocityDecay;
        this._simulationFunc.velocityDecay(vD);
    }

    preventNodesFromScaping() {
        const maxVelocity = this._maxVelocity;
        const nodes = this._nodes;
        for (let i = 0; i < nodes.length; i++) {
            let node = nodes[i];
            const velocity = this._velocityArray[i];
            const itIsScaping = velocity > maxVelocity;
            if (itIsScaping) {
                node.vx = 0;
                node.vy = 0;
                this._velocityDecay = 0.9999;
                this.updateVelocityDecay();
            }
        }
    }
}
